import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { complementRacemapColor } from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { EventTimes } from '../../BasicComponents/EventTimes/index';
import { LoadingIndicator } from '../../BasicComponents/LoadingIndicator/index';
import { HStack } from '../../BasicComponents/MetaComponent/index';
import { useLeaderboardEnvironmentInfo } from '../utils';
import { SelectedEventChooser } from './SelectedEventChooser';

interface Props {
  events: Immutable<Array<RacemapEvent>>;
  parentEvent?: Immutable<RacemapEvent>;
  selectedEventId?: string | null;

  showDates?: boolean;
  loading?: boolean;
  onSelectedEventChange?: (newEventId: string, newSlug?: string) => void;
}

export const LeaderboardHeader: FC<Props> = ({
  events,
  parentEvent,
  selectedEventId,
  onSelectedEventChange,
  loading = false,
}) => {
  const { isEmbedded } = useLeaderboardEnvironmentInfo();
  const topEvent = getTopEvent(events, parentEvent);
  const selectedEvent = getSelectedEvent(events, selectedEventId);
  const eventName = topEvent?.name;
  const leaderboardHeaderColor =
    topEvent?.modules?.timing?.leaderboard?.headerColor ?? `${RacemapColors.CloudBlue}90`;
  const leaderboardHeadlineColor = complementRacemapColor(leaderboardHeaderColor, 0.9);
  const timesColor = complementRacemapColor(leaderboardHeaderColor, 0.7);

  const withSelector =
    parentEvent != null && selectedEvent != null && onSelectedEventChange != null;

  if (topEvent == null || isEmbedded) return null;

  return (
    <Container>
      <TopEvent backgroundColor={leaderboardHeaderColor}>
        <TitleAndDatesContainer>
          <Title
            id={TestSelectors.leaderboard.header.eventTitle.toString()}
            color={leaderboardHeadlineColor}
          >
            {eventName}
          </Title>
          <EventTimes event={topEvent} color={timesColor} />
        </TitleAndDatesContainer>
        <LogoAndLoading>
          <LoadingContainer withSelector={withSelector}>
            <LoadingIndicator isLoading={loading} />
          </LoadingContainer>
          <EventLogo>
            <img src={topEvent?.images?.app} height={80} alt="Event Logo" />
          </EventLogo>
        </LogoAndLoading>
      </TopEvent>
      {events.length > 1 && selectedEvent != null && onSelectedEventChange != null && (
        <SelectedEventChooser
          selectedEventId={selectedEvent.id}
          events={events}
          onEventChange={onSelectedEventChange}
        />
      )}
    </Container>
  );
};

function getTopEvent(
  childEvents: Immutable<Array<RacemapEvent>>,
  parentEvent?: Immutable<RacemapEvent>,
): Immutable<RacemapEvent> | null {
  if (parentEvent != null) return parentEvent;

  if (childEvents.length > 0) {
    return childEvents[0];
  }

  return null;
}

function getSelectedEvent(
  childEvents: Immutable<Array<RacemapEvent>>,
  selectedEventId: string | null | undefined,
): Immutable<RacemapEvent> | null {
  if (selectedEventId != null) {
    const childEvent = childEvents.find((e) => e.id === selectedEventId);

    if (childEvent != null) return childEvent;
  }

  if (childEvents.length > 0) {
    return childEvents[0];
  }

  return null;
}

const Container = styled.div``;

const TopEvent = styled(HStack)<{ backgroundColor: string }>`
  height: 100px;
  background-color: ${(props) => props.backgroundColor};
  z-index: 10;
  padding: 10px;
  flex-direction: row;

  @media screen and (min-width: 576px) {
    padding: 10px 40px;
  }
`;

const TitleAndDatesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  @media screen and (min-width: 576px) {
    width: unset;
  }
`;

const LogoAndLoading = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Title = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1.4em;
  font-weight: bolder;
  line-height: 1em;
  width: 99%;

  padding-bottom: 4px;
  margin-bottom: 0.1em;

  @media screen and (min-width: 576px) {
    width: unset;
    font-size: 2em;
  }
`;

const LoadingContainer = styled.div<{ withSelector: boolean }>`
  margin-right: 50px;

  @media screen and (max-width: 576px) {
    position: absolute;
    top: ${(props) => (props.withSelector ? '160px' : '120px')};
    right: 50%;
    transform: translateX(50%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0;
  }
`;

const EventLogo = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: end;
`;
